import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { OnboardingStep } from './OnboardingStep';
import { OnboardingStepProps } from './OnboardingStep';

export const ReviewStep: React.FC<OnboardingStepProps> = ({
  data,
  onPrevious,
  onComplete,
  isLoading,
}) => {
  const { t } = useLanguage();

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
  };

  const canGoNext = true;

  return (
    <OnboardingStep
      title={t('onboarding.review.title')}
      description={t('onboarding.review.description')}
      onNext={() => {}}
      onPrevious={onPrevious}
      onComplete={handleComplete}
      canGoNext={canGoNext}
      showNext={false}
      showPrevious={true}
      showComplete={true}
      isLoading={isLoading}
    >
      <div className="space-y-6">
        {/* Client Type Summary */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('onboarding.review.clientType')}
          </h3>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-medium">
                  {data.clientType === 'SUPER_TENANT_ADMIN' ? '🏢' : '🏭'}
                </span>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">
                {data.clientType === 'SUPER_TENANT_ADMIN' 
                  ? t('onboarding.review.superTenantAdmin')
                  : data.clientType === 'TENANT_ADMIN'
                  ? t('onboarding.review.tenantAdmin')
                  : t('onboarding.review.organization')
                }
              </p>
              <p className="text-sm text-gray-500">
                {data.clientType === 'SUPER_TENANT_ADMIN' 
                  ? t('onboarding.review.superTenantDescription')
                  : t('onboarding.review.tenantDescription')
                }
              </p>
            </div>
          </div>
        </div>

        {/* Industry & Organization (for tenant clients) */}
        {data.clientType === 'TENANT_ADMIN' && (
          <>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('onboarding.review.industry')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('onboarding.review.industryType')}</p>
                  <p className="text-sm text-gray-900">{data.industryType}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('onboarding.review.organizationSize')}</p>
                  <p className="text-sm text-gray-900">{data.organizationSize}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('onboarding.review.geographicScope')}</p>
                  <p className="text-sm text-gray-900">{data.geographicScope}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('onboarding.review.companyName')}</p>
                  <p className="text-sm text-gray-900">{data.companyName}</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('onboarding.review.organizationDetails')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('onboarding.review.domain')}</p>
                  <p className="text-sm text-gray-900">{data.domain}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('onboarding.review.primaryLocation')}</p>
                  <p className="text-sm text-gray-900">{data.primaryLocation}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('onboarding.review.timeZone')}</p>
                  <p className="text-sm text-gray-900">{data.timeZone}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('onboarding.review.language')}</p>
                  <p className="text-sm text-gray-900">{data.language}</p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Templates Summary */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('onboarding.review.templates')}
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t('onboarding.review.roleTemplates')} ({data.selectedRoleTemplates.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {data.selectedRoleTemplates.map((template, index) => (
                  <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {template}
                  </span>
                ))}
              </div>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t('onboarding.review.userTemplates')} ({data.selectedUserTemplates.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {data.selectedUserTemplates.map((template, index) => (
                  <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {template}
                  </span>
                ))}
              </div>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t('onboarding.review.hierarchyTemplates')} ({data.selectedHierarchyTemplates.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {data.selectedHierarchyTemplates.map((template, index) => (
                  <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {template}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Customizations Summary */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('onboarding.review.customizations')}
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-gray-700">
                {t('onboarding.review.roleCustomizations')}
              </p>
              <p className="text-sm text-gray-600">
                {Object.keys(data.customizations?.roleCustomizations || {}).length} {t('onboarding.review.settings')}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                {t('onboarding.review.userCustomizations')}
              </p>
              <p className="text-sm text-gray-600">
                {Object.keys(data.customizations?.userCustomizations || {}).length} {t('onboarding.review.settings')}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                {t('onboarding.review.hierarchyCustomizations')}
              </p>
              <p className="text-sm text-gray-600">
                {Object.keys(data.customizations?.hierarchyCustomizations || {}).length} {t('onboarding.review.settings')}
              </p>
            </div>
          </div>
        </div>

        {/* Setup Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-4">
            {t('onboarding.review.setupSummary')}
          </h3>
          <div className="space-y-2">
            <div className="flex items-center text-sm text-blue-800">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {data.clientType === 'SUPER_TENANT_ADMIN'
                ? t('onboarding.review.superTenantSetup')
                : t('onboarding.review.tenantSetup')
              }
            </div>
            <div className="flex items-center text-sm text-blue-800">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('onboarding.review.rolesAndPermissions')}
            </div>
            <div className="flex items-center text-sm text-blue-800">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('onboarding.review.hierarchicalStructure')}
            </div>
            <div className="flex items-center text-sm text-blue-800">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('onboarding.review.accessControls')}
            </div>
          </div>
        </div>
      </div>
    </OnboardingStep>
  );
};
